import React, { useEffect, useState, useCallback } from "react";
import { useLazyQuery } from "@apollo/client";
import Loader from "../../components/loader/loader";
import Table from "../../components/table/table";
import TableCellRenderer from "../../components/table/table-cell-renderer";
import {
  METER_TELEMETRY_STATES,
  METER_TELEMETRY_STATE_VALUES,
} from "../../utils/meter-telemetry-states";
import { dateToString } from "../../utils/date-converter";
import Button from "../../components/Button/Button";
import DebouncedInput from "../../components/debounced-input/debounced-input";
import Modal from "../../components/modal/Modal";
import { GET_LATEST_METER_TELEMETRY } from "../../graphql/queries/latestMeterTelemetry";
import { SKIP, FIRST } from "../../utils/pagination";
import AddTowerModal from "../../components/add-tower/add-tower";
import TowersFilters from "../../components/towers-filters/towers-filters";
import hasValue from "../../utils/hasValue";
import AddTowerSuccess from "../../components/add-tower-success/add-tower-success";
import ManualOverrideModal from "../../components/over-rides/overides";
import UpdateEnergyTrading from "../../components/energy-trading/energy-trading";
import SetExpiry from "../../components/set-expiry/set-expiry";
import useUserRoleStore from "../../store/user-role";
import { isAdmin } from "../../utils/roles";

const TowersPage = () => {
  const [searchValue, setSearchValue] = useState("");
  const [manualOverrideModal, setManualOverrideModal] = useState(false);
  const [updateCreditModal, setUpdateCreditModal] = useState(false);
  const [updateEnergyTrading, setUpdateEnergyTrading] = useState(false);
  const [setExpiryModal, setSetExpiryModal] = useState(false);
  const [sortField, setSortField] = useState("client__name"); // default sort field
  const [sortOrder, setSortOrder] = useState("asc"); // default sort order
  const [addTowerModal, setAddTowerModal] = useState(false);
  const [addTowerSuccessModal, setAddTowerSuccessModal] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    hasNextPage: undefined,
    hasPreviousPage: undefined,
    totalPages: undefined,
    totalRecords: undefined,
    currentPage: 1,
  });
  const [meterState, setMeterState] = useState(undefined);
  const [filtersModal, setFiltersModal] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});

  const getStatusValues = (status) => {
    if (!status?.length) {
      return undefined;
    }
    return status.map((item) => METER_TELEMETRY_STATE_VALUES[item]);
  };

  const getTowerValues = (towers) => {
    if (!towers?.length > 0) {
      return undefined;
    }
    return towers;
  };

  const [
    fetchLatestMeterTelemetry,
    { data, loading, error, startPolling, stopPolling },
  ] = useLazyQuery(GET_LATEST_METER_TELEMETRY, {
    variables: {
      search: searchValue,
      state: getStatusValues(appliedFilters.status),
      tower: getTowerValues(appliedFilters.towers),
      first: FIRST,
      skip: (paginationInfo.currentPage - 1) * SKIP,
      sortField: sortField,
      sortOrder: sortOrder,
    },
    fetchPolicy: "network-only",
  });

  // Adjusted sortFieldMapping to match the backend field names directly
  const sortFieldMapping = {
    client__name: "client__name",
    client__clientId: "client__client_id",
    client__balance: "client__balance",
    client__expiry_date: "client__expiry_date",
    channel: "channel",
  };

  const canManualOverride = () => {
    return true;
  };

  const canUpdateCredit = () => {
    return true;
  };

  const onUpdateCredit = () => {
    setUpdateCreditModal(true);
  };

  const onCloseUpdateCreditModal = () => {
    setUpdateCreditModal(false);
  };

  const onCloseManualOverride = () => {
    setManualOverrideModal(false);
  };

  const onCloseEnergyTradingUpdate = () => {
    setUpdateEnergyTrading(false);
  };

  const onCloseSetExpiry = () => {
    setSetExpiryModal(false);
  };

  const [selectedRow, setSelectedRow] = useState([]);

  const onManualOverride = (data) => {
    setSelectedRow({
      id: data.id,
      state: METER_TELEMETRY_STATE_VALUES[data.state],
    });
    setManualOverrideModal(true);
  };

  const handleInputChange = useCallback((e) => {
    setSearchValue(e.target.value);
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  }, []);

  useEffect(() => {
    fetchLatestMeterTelemetry();
    startPolling(30000);
    return () => stopPolling(); // Cleanup polling when component unmounts
  }, [
    searchValue,
    meterState,
    paginationInfo.currentPage,
    sortField,
    sortOrder,
  ]);

  useEffect(() => {
    if (data) {
      setPaginationInfo((prev) => ({
        ...prev,
        currentPage: data.latestMeterTelemetries.currentPage,
        hasNextPage: data.latestMeterTelemetries.hasNextPage,
        hasPreviousPage: data.latestMeterTelemetries.hasPreviousPage,
        totalPages: data.latestMeterTelemetries.totalPages,
        totalRecords: data.latestMeterTelemetries.totalRecords,
      }));
    }
  }, [data]);

  const onUpdateEnergyTrading = (data) => {
    setSelectedRow({
      id: data?.client?.id,
      energyTrading: data?.client?.energyTradingEnabled,
    });
    setUpdateEnergyTrading(true);
  };

  const onSetExpiry = (data) => {
    setSelectedRow({
      id: data?.client?.id,
      expiryDate: data?.client?.expiryDate,
    });
    setSetExpiryModal(true);
  };

  // Staff read the meter summary but do not act on it. An empty list drops the
  // whole Actions column rather than leaving a row of buttons that refuse.
  const { role } = useUserRoleStore();

  const tableActions = !isAdmin(role)
    ? []
    : [
        {
          name: "Manual Override",
          onClick: onManualOverride,
          shouldShow: canManualOverride,
        },
        {
          name: "Add/Remove Credit",
          onClick: onUpdateCredit,
          shouldShow: canUpdateCredit,
        },
        {
          name: "Update Energy Trading",
          onClick: onUpdateEnergyTrading,
          shouldShow: canUpdateCredit,
        },
        {
          name: "Set Expiry",
          onClick: onSetExpiry,
          shouldShow: canUpdateCredit,
        },
      ];

  const onAddTower = () => {
    setAddTowerModal((prev) => !prev);
  };

  if (error) {
    return <div className="display-page__error">Something went wrong</div>;
  }

  const onNextPageClick = () => {
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: prev.currentPage + 1,
    }));
  };

  const onPrevPageClick = () => {
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: prev.currentPage - 1,
    }));
  };

  const handleSortChange = (field) => {
    const sortFieldKey = sortFieldMapping[field] || field;
    const isAsc = sortField === sortFieldKey && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortField(sortFieldKey);
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: 1, // Reset pagination when sort changes
    }));
  };

  const onClearFilters = () => {
    setMeterState(undefined);
    setSearchValue("");
    setAppliedFilters({});
    fetchLatestMeterTelemetry();
  };

  const onAddTowerClose = () => {
    setAddTowerModal(false);
  };

  const onAddTowerSuccessModalClose = () => {
    setAddTowerSuccessModal(false);
  };

  const onFilter = () => {
    setFiltersModal((prev) => !prev);
  };
  const onApplyFilters = () => {
    setFiltersModal(false);
    fetchLatestMeterTelemetry();
  };

  const onApplyOverride = () => {
    setManualOverrideModal(false);
    fetchLatestMeterTelemetry();
  };

  const onApplyEnergyTradingUpdate = () => {
    setUpdateEnergyTrading(false);
    fetchLatestMeterTelemetry();
  };

  const onApplySetExpiry = () => {
    setSetExpiryModal(false);
    fetchLatestMeterTelemetry();
  };

  return (
    <div className="display-page">
      <Modal
        modal={updateCreditModal}
        onClose={onCloseUpdateCreditModal}
        modalSize="large"
      >
        <>Update Credit function</>
      </Modal>
      <Modal
        modal={manualOverrideModal}
        onClose={onCloseManualOverride}
        modalSize="large"
      >
        <>
          <ManualOverrideModal
            onClose={onCloseManualOverride}
            onApply={onApplyOverride}
            selectedData={selectedRow}
          />
        </>
      </Modal>
      <Modal
        modal={updateEnergyTrading}
        onClose={onCloseEnergyTradingUpdate}
        modalSize="large"
      >
        <>
          <UpdateEnergyTrading
            onClose={onCloseEnergyTradingUpdate}
            onApply={onApplyEnergyTradingUpdate}
            selectedData={selectedRow}
          />
        </>
      </Modal>
      <Modal
        modal={setExpiryModal}
        onClose={onCloseSetExpiry}
        modalSize="large"
      >
        <>
          <SetExpiry
            onClose={onCloseSetExpiry}
            onApply={onApplySetExpiry}
            selectedData={selectedRow}
          />
        </>
      </Modal>
      <Modal
        modal={filtersModal}
        onClose={onFilter}
        modalSize="large-no-scroll"
      >
        <>
          <TowersFilters
            onFilter={onFilter}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            onApplyFilters={onApplyFilters}
          />
        </>
      </Modal>
      <Modal modal={addTowerModal} onClose={onAddTowerClose} modalSize="large">
        <>
          <AddTowerModal onCancel={onAddTowerClose} />
        </>
      </Modal>

      <Modal
        modal={addTowerSuccessModal}
        onClose={onAddTowerSuccessModalClose}
        modalSize="large"
      >
        <>
          <AddTowerSuccess onCancel={onAddTowerSuccessModalClose} />
        </>
      </Modal>
      <div className="display-page__header">
        <div className="display-page__search-input">
          <img
            src="/images/search.svg"
            alt="search-icon"
            className="display-page__search-icon"
          />
          <DebouncedInput
            className="input-group input-group__search"
            debounceTimeout={400}
            minLength={3}
            onChange={handleInputChange}
            value={searchValue}
          />
        </div>
        <div className="display-page__header-column display-page__filter">
          <Button
            onClick={onFilter}
            name={"Filter"}
            className={"button--primary"}
          />
        </div>
        {isAdmin(role) && (
          <div className="display-page__action-button">
            <Button
              onClick={onAddTower}
              name={"+ Add Tower"}
              className={"button--primary"}
            />
          </div>
        )}
      </div>
      <div className="display-page__sub-header">
        {hasValue(appliedFilters) && (
          <div className="display-page__filters">
            <div className="display-page__sub-header-text">
              Applied Filters:
              <div className="display-page__sub-header-text">
                {appliedFilters?.status?.map((item) => item).join(", ")},{" "}
                {appliedFilters?.towers?.map((item) => item).join(", ")}
              </div>
            </div>
            <div
              onClick={onClearFilters}
              className="display-page__filters-clear"
            >
              Clear Filters
            </div>
          </div>
        )}
      </div>
      <div className="display-page__info-wrapper">
        {data?.latestMeterTelemetries?.items.count > 0 && (
          <>
            (
            {paginationInfo.currentPage && paginationInfo.totalPages && (
              <>
                Displaying{" "}
                <span className="display-page__info--bold">
                  {paginationInfo.currentPage === 1
                    ? 1
                    : (paginationInfo.currentPage - 1) * SKIP + 1}{" "}
                </span>
                to{" "}
                <span className="display-page__info--bold">
                  {Math.min(
                    paginationInfo.currentPage * SKIP,
                    paginationInfo.totalRecords
                  )}
                </span>{" "}
                of{" "}
                <span className="display-page__info--bold">
                  {paginationInfo.totalRecords}
                </span>{" "}
                total.
              </>
            )}
            )
          </>
        )}
      </div>
      <div className="display-page__table">
        {loading && !data ? (
          <Loader sizeClass={"size--medium"} />
        ) : (
          <Table
            data={data?.latestMeterTelemetries?.items || []}
            columns={[
              {
                field: "channel",
                title: "Channel",
                showOnMobile: true,
                hideMobileUp: false,
                sortable: true,
                render: ({ tower, client: { channel } }) => {
                  return (
                    <TableCellRenderer
                      firstItem={`${tower.zonkeId}-${channel}`}
                    />
                  );
                },
              },
              {
                field: "client__name",
                title: "Name",
                showOnMobile: true,
                hideMobileUp: false,
                sortable: true,
                render: ({ client }) => {
                  return (
                    <TableCellRenderer
                      firstItem={`${client.name} ${client.surname}`}
                    />
                  );
                },
              },
              {
                field: "client__client_id",
                title: "Client ID",
                showOnMobile: true,
                hideMobileUp: false,
                sortable: true,
                render: ({ client }) => {
                  return (
                    <TableCellRenderer firstItem={`${client?.clientId}`} />
                  );
                },
              },
              {
                field: "state",
                title: "Status",
                showOnMobile: true,
                hideMobileUp: false,
                render: ({ state }) => {
                  return (
                    <TableCellRenderer
                      firstItem={`${METER_TELEMETRY_STATES[state]}`}
                      firstItemClass={
                        state === METER_TELEMETRY_STATES.OFF ? "danger" : ""
                      }
                    />
                  );
                },
              },
              {
                field: "client__expiry_date",
                title: "Expiry Date",
                showOnMobile: true,
                hideMobileUp: false,
                sortable: true,
                render: ({ client }) => {
                  return (
                    <TableCellRenderer
                      firstItem={dateToString(client.expiryDate)}
                    />
                  );
                },
              },
              {
                field: "client__balance",
                title: "Balance",
                showOnMobile: true,
                hideMobileUp: false,
                sortable: true,
                render: ({ client }) => {
                  return (
                    <TableCellRenderer
                      firstItem={`R${client.balance.toLocaleString()}`}
                    />
                  );
                },
              },
              {
                field: "client__whBalance",
                title: "WH Balance",
                showOnMobile: true,
                hideMobileUp: false,
                render: ({ client }) => {
                  return (
                    <TableCellRenderer firstItem={client.whBalance ?? 0} />
                  );
                },
              },
              {
                field: "client__energyTrading",
                title: "Energy Trading",
                showOnMobile: true,
                hideMobileUp: false,
                render: ({ client }) => {
                  return (
                    <TableCellRenderer
                      firstItem={
                        client.energyTradingEnabled ? "Enabled" : "Disabled"
                      }
                    />
                  );
                },
              },
            ]}
            actions={tableActions}
            paged
            hasNextPage={paginationInfo.hasNextPage}
            hasPrevPage={paginationInfo.hasPreviousPage}
            onNextPage={onNextPageClick}
            onPrevPage={onPrevPageClick}
            sortConfig={{ key: sortField, direction: sortOrder }}
            onSortChange={handleSortChange}
          />
        )}
      </div>
    </div>
  );
};

export default TowersPage;
